import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';
import '../styles/ProfileModal.css';

export default function ProfileModal({ onClose }) {
  const { user, refreshUser } = useAuth();
  const { t } = useTranslation();
  const [tab, setTab] = useState('info'); // 'info' | 'password'
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState(null);

  // Profile fields
  const [form, setForm] = useState({
    first_name: '',
    last_name: '',
    company: '',
    phone: '',
    bio: ''
  });

  // Password fields
  const [pwForm, setPwForm] = useState({
    current_password: '',
    new_password: '',
    confirm_password: ''
  });

  useEffect(() => {
    if (user) {
      setForm({
        first_name: user.first_name || '',
        last_name: user.last_name || '',
        company: user.company || '',
        phone: user.phone || '',
        bio: user.bio || ''
      });
    }
  }, [user]);

  const handleSaveProfile = async (e) => {
    e.preventDefault();
    setSaving(true);
    setMessage(null);
    try {
      await api.put('/auth/profile', form);
      setMessage({ type: 'success', text: t('profile.messages.profileUpdated') });
      if (refreshUser) refreshUser();
    } catch (err) {
      setMessage({ type: 'error', text: err.response?.data?.error || t('profile.messages.errorUpdate') });
    } finally {
      setSaving(false);
    }
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    if (pwForm.new_password !== pwForm.confirm_password) {
      setMessage({ type: 'error', text: t('profile.messages.passwordMismatch') });
      return;
    }
    if (pwForm.new_password.length < 8) {
      setMessage({ type: 'error', text: t('profile.messages.passwordTooShort') });
      return;
    }
    setSaving(true);
    setMessage(null);
    try {
      await api.put('/auth/password', {
        current_password: pwForm.current_password,
        new_password: pwForm.new_password
      });
      setMessage({ type: 'success', text: t('profile.messages.passwordChanged') });
      setPwForm({ current_password: '', new_password: '', confirm_password: '' });
    } catch (err) {
      setMessage({ type: 'error', text: err.response?.data?.error || t('profile.messages.errorPassword') });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="profile-overlay" onClick={onClose}>
      <div className="profile-modal" onClick={e => e.stopPropagation()}>
        <div className="profile-modal-header">
          <h2>{t('profile.title')}</h2>
          <button className="profile-modal-close" onClick={onClose}>✕</button>
        </div>

        {/* Profile avatar + email */}
        <div className="profile-identity">
          <div className="profile-avatar-lg">
            {user?.email?.[0]?.toUpperCase() ?? '?'}
          </div>
          <div className="profile-identity-info">
            <strong>{form.first_name || form.last_name ? `${form.first_name} ${form.last_name}`.trim() : t('profile.defaultUser')}</strong>
            <span>{user?.email}</span>
            <span className="profile-plan-badge">{user?.subscription_plan || 'free'}</span>
          </div>
        </div>

        {/* Tabs */}
        <div className="profile-tabs">
          <button className={`profile-tab ${tab === 'info' ? 'active' : ''}`} onClick={() => { setTab('info'); setMessage(null); }}>
            {t('profile.tabs.info')}
          </button>
          <button className={`profile-tab ${tab === 'password' ? 'active' : ''}`} onClick={() => { setTab('password'); setMessage(null); }}>
            {t('profile.tabs.password')}
          </button>
        </div>

        {/* Message */}
        {message && (
          <div className={`profile-message profile-message-${message.type}`}>
            {message.text}
          </div>
        )}

        {/* Info form */}
        {tab === 'info' && (
          <form onSubmit={handleSaveProfile} className="profile-form">
            <div className="profile-form-row">
              <div className="profile-field">
                <label>{t('profile.fields.firstName')}</label>
                <input value={form.first_name} onChange={e => setForm({ ...form, first_name: e.target.value })} placeholder={t('profile.fields.firstNamePlaceholder')} />
              </div>
              <div className="profile-field">
                <label>{t('profile.fields.lastName')}</label>
                <input value={form.last_name} onChange={e => setForm({ ...form, last_name: e.target.value })} placeholder={t('profile.fields.lastNamePlaceholder')} />
              </div>
            </div>
            <div className="profile-field">
              <label>{t('profile.fields.company')}</label>
              <input value={form.company} onChange={e => setForm({ ...form, company: e.target.value })} placeholder={t('profile.fields.companyPlaceholder')} />
            </div>
            <div className="profile-field">
              <label>{t('profile.fields.phone')}</label>
              <input value={form.phone} onChange={e => setForm({ ...form, phone: e.target.value })} placeholder={t('profile.fields.phonePlaceholder')} />
            </div>
            <div className="profile-field">
              <label>{t('profile.fields.bio')}</label>
              <textarea value={form.bio} onChange={e => setForm({ ...form, bio: e.target.value })} placeholder={t('profile.fields.bioPlaceholder')} rows={3} />
            </div>
            <div className="profile-actions">
              <button type="button" className="btn btn-outline" onClick={onClose}>{t('profile.cancel')}</button>
              <button type="submit" className="btn btn-primary" disabled={saving}>
                {saving ? t('profile.saving') : t('profile.save')}
              </button>
            </div>
          </form>
        )}

        {/* Password form */}
        {tab === 'password' && (
          <form onSubmit={handleChangePassword} className="profile-form">
            <div className="profile-field">
              <label>{t('profile.fields.currentPassword')}</label>
              <input type="password" value={pwForm.current_password} onChange={e => setPwForm({ ...pwForm, current_password: e.target.value })} required />
            </div>
            <div className="profile-field">
              <label>{t('profile.fields.newPassword')}</label>
              <input type="password" value={pwForm.new_password} onChange={e => setPwForm({ ...pwForm, new_password: e.target.value })} required minLength={8} />
            </div>
            <div className="profile-field">
              <label>{t('profile.fields.confirmPassword')}</label>
              <input type="password" value={pwForm.confirm_password} onChange={e => setPwForm({ ...pwForm, confirm_password: e.target.value })} required minLength={8} />
            </div>
            <div className="profile-actions">
              <button type="button" className="btn btn-outline" onClick={onClose}>{t('profile.cancel')}</button>
              <button type="submit" className="btn btn-primary" disabled={saving}>
                {saving ? t('profile.changing') : t('profile.changePassword')}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
